import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  Platform,
} from 'react-native';
import MaterialIcons from '@expo/vector-icons/MaterialIcons';
import { getApiUrl } from '../utils/apiConfig';
import {
  exercisesListUrl,
  exerciseTypesByCourseUrl,
} from '../utils/exerciseApi';

function DropdownField({ label, value, placeholder, open, onToggle, theme, styles, children }) {
  return (
    <View style={styles.dropdownField}>
      <Text style={[styles.dropdownLabel, { color: theme.colors.text }]}>{label}</Text>
      <TouchableOpacity
        style={[styles.dropdownTrigger, { borderColor: theme.colors.border }]}
        onPress={onToggle}
        activeOpacity={0.8}
        {...(Platform.OS === 'web' ? { dataSet: { focusExerciseDropdown: 'true' } } : {})}
      >
        <Text
          style={[
            styles.dropdownTriggerText,
            { color: value ? theme.colors.heading : theme.colors.text, opacity: value ? 1 : 0.65 },
          ]}
          numberOfLines={1}
        >
          {value || placeholder}
        </Text>
        <MaterialIcons
          name={open ? 'keyboard-arrow-up' : 'keyboard-arrow-down'}
          size={20}
          color={theme.colors.text}
        />
      </TouchableOpacity>
      {open ? (
        <View
          style={[styles.dropdownMenu, { borderColor: theme.colors.border, backgroundColor: theme.isDarkMode ? '#1a1a1a' : '#fff' }]}
          {...(Platform.OS === 'web' ? { dataSet: { focusExerciseDropdownMenu: 'true' } } : {})}
        >
          {children}
        </View>
      ) : null}
    </View>
  );
}

export default function FocusExercisePickerRow({
  value,
  onChange,
  onRemove,
  showRemove,
  studentId,
  courses,
  theme,
}) {
  const styles = getStyles(theme);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [exerciseTypes, setExerciseTypes] = useState([]);
  const [selectedExerciseType, setSelectedExerciseType] = useState(null);
  const [exercises, setExercises] = useState([]);
  const [openMenu, setOpenMenu] = useState(null);

  useEffect(() => {
    if (!value?.exercise_id) {
      setSelectedCourse(null);
      setSelectedExerciseType(null);
      setExerciseTypes([]);
      setExercises([]);
      return;
    }
    if (value.course_id && value.course_name) {
      setSelectedCourse({ id: value.course_id, course_name: value.course_name });
    }
    if (value.exercise_type_id && value.exercise_type_name) {
      setSelectedExerciseType({
        id: value.exercise_type_id,
        exercise_type_name: value.exercise_type_name,
      });
    }
  }, [value?.exercise_id, value?.course_id, value?.course_name, value?.exercise_type_id, value?.exercise_type_name]);

  useEffect(() => {
    if (!studentId || !selectedCourse?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const typesUrl = exerciseTypesByCourseUrl(selectedCourse.id, {
          viewerId: studentId,
          studentId,
        });
        const response = await fetch(typesUrl);
        if (!response.ok) return;
        const data = await response.json();
        if (!cancelled) setExerciseTypes(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setExerciseTypes([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [studentId, selectedCourse?.id]);

  useEffect(() => {
    if (!studentId || !selectedExerciseType?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const listUrl = exercisesListUrl({
          exerciseTypeId: selectedExerciseType.id,
          courseId: selectedCourse?.id,
          studentId,
          viewerId: studentId,
        });
        const response = await fetch(listUrl);
        if (!response.ok) return;
        const data = await response.json();
        if (!cancelled) setExercises(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setExercises([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [studentId, selectedCourse?.id, selectedExerciseType?.id]);

  const closeMenus = useCallback(() => setOpenMenu(null), []);

  const handleCourseSelect = async (course) => {
    setSelectedCourse(course);
    setSelectedExerciseType(null);
    setExercises([]);
    onChange(null);
    closeMenus();
    try {
      const typesUrl = exerciseTypesByCourseUrl(course.id, {
        viewerId: studentId,
        studentId,
      });
      const response = await fetch(typesUrl);
      if (response.ok) {
        const data = await response.json();
        setExerciseTypes(Array.isArray(data) ? data : []);
      } else {
        setExerciseTypes([]);
      }
    } catch {
      setExerciseTypes([]);
    }
  };

  const handleExerciseTypeSelect = async (exerciseType) => {
    setSelectedExerciseType(exerciseType);
    onChange(null);
    closeMenus();
    try {
      const listUrl = exercisesListUrl({
        exerciseTypeId: exerciseType.id,
        courseId: selectedCourse?.id,
        studentId,
        viewerId: studentId,
      });
      const response = await fetch(listUrl);
      if (response.ok) {
        const data = await response.json();
        setExercises(Array.isArray(data) ? data : []);
      } else {
        setExercises([]);
      }
    } catch {
      setExercises([]);
    }
  };

  const handleExerciseSelect = (exercise) => {
    onChange({
      exercise_id: exercise.id,
      exercise_name: exercise.exercise_name,
      course_id: selectedCourse?.id ?? null,
      course_name: selectedCourse?.course_name ?? null,
      exercise_type_id: selectedExerciseType?.id ?? exercise.exercise_type_id ?? null,
      exercise_type_name:
        selectedExerciseType?.exercise_type_name ?? exercise.exercise_type_name ?? null,
    });
    closeMenus();
  };

  const selectedExerciseLabel = value?.exercise_name || null;

  return (
    <View style={styles.row}>
      <View style={styles.rowFields}>
        <DropdownField
          label="Course"
          value={selectedCourse?.course_name}
          placeholder="Select course"
          open={openMenu === 'course'}
          onToggle={() => setOpenMenu((prev) => (prev === 'course' ? null : 'course'))}
          theme={theme}
          styles={styles}
        >
          <ScrollView style={styles.dropdownScroll} nestedScrollEnabled keyboardShouldPersistTaps="handled">
            {courses.map((course) => (
              <TouchableOpacity
                key={course.id}
                style={styles.dropdownOption}
                onPress={() => void handleCourseSelect(course)}
              >
                <Text style={[styles.dropdownOptionText, { color: theme.colors.text }]}>
                  {course.course_name}
                </Text>
              </TouchableOpacity>
            ))}
          </ScrollView>
        </DropdownField>

        {selectedCourse ? (
          <DropdownField
            label="Exercise type"
            value={selectedExerciseType?.exercise_type_name}
            placeholder="Select type"
            open={openMenu === 'type'}
            onToggle={() => setOpenMenu((prev) => (prev === 'type' ? null : 'type'))}
            theme={theme}
            styles={styles}
          >
            <ScrollView style={styles.dropdownScroll} nestedScrollEnabled keyboardShouldPersistTaps="handled">
              {exerciseTypes.length === 0 ? (
                <Text style={[styles.dropdownEmpty, { color: theme.colors.text }]}>
                  No exercise types for this course.
                </Text>
              ) : (
                exerciseTypes.map((type) => (
                  <TouchableOpacity
                    key={type.id}
                    style={styles.dropdownOption}
                    onPress={() => void handleExerciseTypeSelect(type)}
                  >
                    <Text style={[styles.dropdownOptionText, { color: theme.colors.text }]}>
                      {type.exercise_type_name}
                    </Text>
                  </TouchableOpacity>
                ))
              )}
            </ScrollView>
          </DropdownField>
        ) : null}

        {selectedExerciseType ? (
          <DropdownField
            label="Exercise"
            value={selectedExerciseLabel}
            placeholder="Select exercise"
            open={openMenu === 'exercise'}
            onToggle={() => setOpenMenu((prev) => (prev === 'exercise' ? null : 'exercise'))}
            theme={theme}
            styles={styles}
          >
            <ScrollView style={styles.dropdownScroll} nestedScrollEnabled keyboardShouldPersistTaps="handled">
              {exercises.length === 0 ? (
                <Text style={[styles.dropdownEmpty, { color: theme.colors.text }]}>
                  No exercises for this type.
                </Text>
              ) : (
                exercises.map((exercise) => (
                  <TouchableOpacity
                    key={exercise.id}
                    style={styles.dropdownOption}
                    onPress={() => handleExerciseSelect(exercise)}
                  >
                    <Text style={[styles.dropdownOptionText, { color: theme.colors.text }]}>
                      {exercise.exercise_name}
                    </Text>
                  </TouchableOpacity>
                ))
              )}
            </ScrollView>
          </DropdownField>
        ) : null}
      </View>
      {showRemove ? (
        <TouchableOpacity
          style={styles.removeBtn}
          onPress={onRemove}
          accessibilityLabel="Remove focus exercise"
          hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
          {...(Platform.OS === 'web' ? { dataSet: { focusExerciseRemove: 'true' } } : {})}
        >
          <MaterialIcons name="delete-outline" size={22} color="#c23934" />
        </TouchableOpacity>
      ) : null}
    </View>
  );
}

function getStyles(theme) {
  return StyleSheet.create({
    row: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: 8,
      marginBottom: 16,
      backgroundColor: 'transparent',
    },
    rowFields: {
      flex: 1,
      gap: 10,
      backgroundColor: 'transparent',
    },
    dropdownField: {
      position: 'relative',
      zIndex: 1,
      backgroundColor: 'transparent',
    },
    dropdownLabel: {
      fontSize: 12,
      fontWeight: '600',
      marginBottom: 4,
      backgroundColor: 'transparent',
    },
    dropdownTrigger: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      borderWidth: 1,
      borderRadius: 6,
      paddingVertical: 8,
      paddingHorizontal: 10,
      backgroundColor: theme.isDarkMode ? '#1a1a1a' : '#ffffff',
    },
    dropdownTriggerText: {
      flex: 1,
      fontSize: 14,
      marginRight: 8,
      backgroundColor: 'transparent',
    },
    dropdownMenu: {
      marginTop: 4,
      borderWidth: 1,
      borderRadius: 6,
      maxHeight: 180,
      overflow: 'hidden',
    },
    dropdownScroll: {
      maxHeight: 180,
    },
    dropdownOption: {
      paddingVertical: 10,
      paddingHorizontal: 12,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: theme.colors.border,
      backgroundColor: 'transparent',
    },
    dropdownOptionText: {
      fontSize: 14,
      backgroundColor: 'transparent',
    },
    dropdownEmpty: {
      padding: 12,
      fontSize: 13,
      fontStyle: 'italic',
      backgroundColor: 'transparent',
    },
    removeBtn: {
      paddingTop: 22,
      paddingHorizontal: 4,
      backgroundColor: 'transparent',
    },
  });
}
